import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Textarea, Modal, DataTable, Plus, toast } from "@/pages/app/kit";
import { StatusBadge, DemoBadge } from "@/components/shared";

export default function SettlementsPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const { user } = useAuth();
  const isManager = ["coordinator", "admin", "super_admin"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [crops, setCrops] = useState([]);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cForm, setCForm] = useState({ crop_id: "", buyer_gross_price: 0, quantity_accepted: 0 });
  const [adj, setAdj] = useState({ type: "Grading adjustment", amount: 0, reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settlements").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); if (isManager) api.get("/crops").then((r) => setCrops(r.data)).catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/settlements", { crop_id: cForm.crop_id, buyer_gross_price: parseFloat(cForm.buyer_gross_price)||0, quantity_accepted: parseFloat(cForm.quantity_accepted)||0 });
      toast.success(t("cta.create")); setCreateOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addAdj = async (e) => {
    e.preventDefault();
    if (!adj.reason.trim()) { toast.error(L("Powód jest wymagany","Reason required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/settlements/${detail.id}/adjustment`, { type: adj.type, amount: parseFloat(adj.amount)||0, reason: adj.reason });
      setDetail(data); setAdj({ type: "Grading adjustment", amount: 0, reason: "" }); load();
      toast.success("OK");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cols = [
    { key: "crop_product", label: "Crop", render: (r) => <div><span className="font-semibold">{r.crop_product}</span>{r.is_demo && <DemoBadge className="ml-2" />}<div className="text-xs text-mutedtext">{r.farmer_name}</div></div> },
    { key: "gross_total", label: t("settlement.grossTotal"), render: (r) => `${r.gross_total?.toLocaleString()} PLN` },
    { key: "total_deductions", label: t("settlement.deductions"), render: (r) => `${r.total_deductions?.toLocaleString()} PLN` },
    { key: "total_farmer_payment", label: t("settlement.payment"), render: (r) => <span className="font-bold text-forest">{r.total_farmer_payment?.toLocaleString()} PLN</span> },
    { key: "status", label: t("crop.status"), render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", label: "", render: (r) => <button className="text-xs font-semibold text-forest hover:underline" onClick={() => setDetail(r)} data-testid={`settlement-view-${r.id}`}>{L("Szczegóły","Details")}</button> },
  ];

  return (
    <div>
      <PageTitle title={t("dashboard.settlements")} subtitle={t("settlement.transparency")}
        action={isManager && <Btn icon={Plus} onClick={() => setCreateOpen(true)} data-testid="add-settlement-btn">{t("cta.create")}</Btn>} />
      <DataTable columns={cols} rows={rows} empty={t("empty")} testid="settlements-table" />

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t("dashboard.settlements")}>
        <form onSubmit={create} className="space-y-4">
          <Field label="Crop" required><Select value={cForm.crop_id} onChange={(e) => setCForm({ ...cForm, crop_id: e.target.value })} required><option value="">—</option>{crops.map((c) => <option key={c.id} value={c.id}>{c.product} — {c.owner_name}</option>)}</Select></Field>
          <Field label={t("settlement.gross")} required><Input type="number" step="0.01" value={cForm.buyer_gross_price} onChange={(e) => setCForm({ ...cForm, buyer_gross_price: e.target.value })} /></Field>
          <Field label={t("settlement.quantity")} required><Input type="number" value={cForm.quantity_accepted} onChange={(e) => setCForm({ ...cForm, quantity_accepted: e.target.value })} /></Field>
          <Btn type="submit" disabled={busy}>{busy?"…":t("cta.create")}</Btn>
        </form>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={`${L("Rozliczenie","Settlement")} · ${detail?.crop_product}`} wide>
        {detail && (
          <div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <KV label={t("settlement.gross")} v={`${detail.buyer_gross_price} PLN × ${detail.quantity_accepted} ${detail.unit}`} />
              <KV label={t("settlement.grossTotal")} v={`${detail.gross_total?.toLocaleString()} PLN`} />
            </div>
            <h4 className="mt-5 font-heading font-bold text-charcoal">{t("settlement.deductions")}</h4>
            <table className="mt-2 w-full text-sm">
              <thead><tr className="border-b border-subtle text-left text-mutedtext"><th className="py-1">{t("settlement.type")}</th><th>{t("settlement.reason")}</th><th>User</th><th className="text-right">{t("settlement.amount")}</th></tr></thead>
              <tbody>
                {(detail.adjustments||[]).map((a) => (
                  <tr key={a.id} className="border-b border-subtle last:border-0"><td className="py-1.5">{a.type}</td><td className="text-mutedtext">{a.reason}</td><td className="text-xs text-mutedtext">{a.user}</td><td className="text-right">{a.amount.toLocaleString()} PLN</td></tr>
                ))}
                {(!detail.adjustments || detail.adjustments.length===0) && <tr><td colSpan={4} className="py-2 text-mutedtext">{t("empty")}</td></tr>}
              </tbody>
            </table>
            <div className="mt-4 flex justify-between rounded-md bg-forest/5 p-3">
              <span className="font-semibold text-charcoal">{t("settlement.payment")}</span>
              <span className="font-heading text-lg font-bold text-forest">{detail.total_farmer_payment?.toLocaleString()} PLN</span>
            </div>

            {isManager && (
              <form onSubmit={addAdj} className="mt-6 space-y-3 border-t border-subtle pt-4">
                <h4 className="font-heading font-bold text-charcoal">{t("settlement.addAdjustment")}</h4>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label={t("settlement.type")}><Select value={adj.type} onChange={(e) => setAdj({ ...adj, type: e.target.value })}>{["Grading adjustment","Processing cost","Transport cost","Regional-center cost","Reserve contribution","Other approved adjustment"].map((x)=><option key={x}>{x}</option>)}</Select></Field>
                  <Field label={t("settlement.amount")}><Input type="number" step="0.01" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} /></Field>
                  <Field label={t("settlement.reason")} required><Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="adj-reason" /></Field>
                </div>
                <Btn type="submit" disabled={busy} data-testid="add-adj-btn">{busy?"…":t("settlement.addAdjustment")}</Btn>
              </form>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
function KV({ label, v }) { return <div><div className="text-xs text-mutedtext">{label}</div><div className="font-medium text-charcoal">{v}</div></div>; }
